import React from "react";
import { useTranslation } from "react-i18next";
import { useRemoveCartItemFromCart } from "../../hooks/useRemoveCartItemFromCart";
import { useUpdateCartItemQuantity } from "../../hooks/useUpdateCartItemQuantity";
import { useMoveToSavedForLaterFromCart } from "../../../savedForLater/hooks/useMoveToSavedForLaterFromCart";
import { useMoveCartItemToWishlist } from "../../hooks/useMoveCartItemToWishlist";
import { useToggleCompareItem } from "../../../compare/hooks/useToggleCompareItem";
import type { TransformedCartItem } from "../../types/cartApiTypes";
import QuantityControl from "../common/QuantityControl";
import CartItemActions from "../common/CartItemActions";
import { decodeHtmlEntities } from "../../../../../common/helpers/decodeHtmlEntities";

interface CartPanelItemProps {
  item: TransformedCartItem;
}

const CartPanelItem: React.FC<CartPanelItemProps> = ({ item }) => {
  const { t } = useTranslation();

  // --- Logic: If sold individually, force max quantity limit to 1 ---
  const minQuantity = item.quantityLimits.minimum;
  const maxQuantity = item.soldIndividually ? 1 : item.quantityLimits.maximum;

  // --- Consume Custom Hooks ---
  const { quantity, handleQuantityChange, isUpdating } =
    useUpdateCartItemQuantity(item.key, item.quantity);

  const { handleRemoveCartItem: triggerRemove, isRemoving } =
    useRemoveCartItemFromCart();

  const { handleMoveCartItemToWishlist, isMoving } =
    useMoveCartItemToWishlist();

  const { handleMoveToSavedForLater, isMoving: isSaving } =
    useMoveToSavedForLaterFromCart();

  // Use item.id directly. For variations, this is the Variation ID.
  const { handleToggle: handleCompareToggle, isInCompare } =
    useToggleCompareItem(item.id);

  const onRemoveClick = () => {
    triggerRemove({ key: item.key });
  };

  const onMoveToWishlistClick = () => {
    handleMoveCartItemToWishlist(item.key);
  };

  const onSaveForLaterClick = () => {
    handleMoveToSavedForLater(item.key);
  };

  const onQuantityInputChange = (newQuantity: number) => {
    handleQuantityChange(newQuantity);
  };

  const image = item.images.length > 0 ? item.images[0] : null;
  const isLoading = isUpdating || isRemoving || isMoving || isSaving;
  const decodedName = decodeHtmlEntities(item.name);

  // --- BUY X PAY Y PROMOTION LOGIC ---
  const isGift = item.rawPrice === "0";
  const promo = item.buyXPayYPromotion;

  return (
    <li className="cart-panel-item">
      {/* Primary Actions (Wishlist & Remove) */}
      <CartItemActions
        onMoveToWishlist={onMoveToWishlistClick}
        onRemove={onRemoveClick}
        isMoving={isMoving}
        isRemoving={isRemoving}
        itemName={decodedName}
        context="panel"
        placement="top"
      />

      {/* Wrapper for main content */}
      <div className="cart-panel-item__content">
        <div className="cart-panel-item__image">
          <a href={item.permalink}>
            {image ? (
              <img src={image.thumbnail} alt={image.alt || decodedName} />
            ) : (
              <div className="placeholder-image"></div>
            )}
          </a>
        </div>
        <div className="cart-panel-item__details">
          <a href={item.permalink} className="cart-panel-item__name">
            {decodedName}
          </a>

          {/* --- DYNAMIC STOCK BADGES & STATUSES --- */}
          <div
            className="cart-item-stock-status-wrapper"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "4px",
              margin: "2px 0 6px 0",
            }}
          >
            {item.soldIndividually && (
              <span
                style={{
                  fontSize: "10px",
                  color: "var(--color-text-secondary)",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                }}
              >
                {t("cartItem.soldIndividually", "Sold Individually")}
              </span>
            )}
            {item.showBackorderBadge && (
              <span
                style={{
                  fontSize: "10px",
                  color: "var(--color-info)",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                }}
              >
                {t("cartItem.backorder", "On Backorder")}
              </span>
            )}
            {!item.soldIndividually && item.lowStockRemaining !== null && (
              <span
                style={{
                  fontSize: "10px",
                  color: "var(--color-danger)",
                  fontWeight: "bold",
                }}
              >
                {t("productCard.lowStockBadge", {
                  count: item.lowStockRemaining,
                })}
              </span>
            )}
          </div>

          {promo && (
            <span className="cart-item-promo-badge">
              {t("productCard.buyXPayYBadge", {
                buy: promo.buyQty,
                pay: promo.payQty,
                defaultValue: `Buy ${promo.buyQty} Pay ${promo.payQty}!`,
              })}
            </span>
          )}

          {item.variation.length > 0 && (
            <div className="cart-panel-item__meta">
              {item.variation.map((v) => (
                <div key={v.attribute}>
                  <strong>{v.attribute}:</strong> {decodeHtmlEntities(v.value)}
                </div>
              ))}
            </div>
          )}
          <div className="cart-panel-item__footer">
            <div className="cart-panel-item__footer-group">
              <span className="cart-panel-item__footer-label">
                {t("cartOffcanvas.item.price", "Price")}
              </span>
              <span className="cart-panel-item__price">
                {isGift ? (
                  <span className="is-gift">
                    {t("feedback.cart.gift", "Gift!")}
                  </span>
                ) : (
                  // Display the Catalog Price (Original Unit Price)
                  item.catalogPrice
                )}
              </span>
            </div>

            <div className="cart-panel-item__footer-group cart-panel-item__footer-group--quantity">
              <span className="cart-panel-item__footer-label">
                {t("cartOffcanvas.item.qty", "Qty")}
              </span>
              {/* The QuantityControl component will sit in the middle */}
              <QuantityControl
                value={quantity}
                onChange={onQuantityInputChange}
                min={minQuantity}
                max={maxQuantity}
                // Disable controller if loading OR if item is sold individually
                disabled={isLoading || item.soldIndividually}
                ariaLabel={`Quantity for ${decodedName}`}
                className="cart-panel-item__quantity-controls"
              />
            </div>

            <div className="cart-panel-item__footer-group">
              <span className="cart-panel-item__footer-label">
                {t("cartOffcanvas.item.total", "Total")}
              </span>
              <span className="cart-panel-item__line-total">
                {isGift ? (
                  <span className="is-gift">
                    {t("feedback.cart.gift", "Gift!")}
                  </span>
                ) : (
                  item.formattedCalculatedLineTotal
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Actions (Save for Later & Compare) */}
      <CartItemActions
        onSaveForLater={onSaveForLaterClick}
        onToggleCompare={handleCompareToggle}
        isInCompare={isInCompare}
        isSaving={isSaving}
        itemName={decodedName}
        context="panel"
        placement="bottom"
      />
    </li>
  );
};

export default CartPanelItem;
